import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/shared/lib/supabase";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { FieldError } from "@/shared/components/ui/FieldError";
import { SecurityNote } from "@/shared/components/ui/security-note";
import { useSignInRoute } from "@/shared/hooks/useSignInRoute";
import { sentryUtils } from "@/shared/utils/sentry";
import { ArrowRight, Loader2 } from "lucide-react";
import FullscreenLayout from "@/pages/layouts/FullScreenLayout";

const ResetPasswordPage = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState("");
  const [isValidLink, setIsValidLink] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signInRoute } = useSignInRoute();

  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange(() => {});

    const checkRecoverySession = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get("access_token");

      if (accessToken) {
        return;
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        setIsValidLink(false);
        setServerError("This reset link has expired or was already used. Request a new one.");
      }
    };

    const timer = setTimeout(() => {
      checkRecoverySession();
    }, 100);

    return () => {
      listener.subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, [searchParams]);

  const handleSubmit = async () => {
    if (!password || !confirmPassword) {
      setValidationError("Enter your new password in both fields");
      return;
    }
    if (password !== confirmPassword) {
      setValidationError("The passwords don't match");
      return;
    }

    setLoading(true);
    setValidationError("");
    setServerError("");
    setSuccess("");

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        setServerError(error.message);
        return;
      }

      await supabase.auth.signOut();

      setSuccess("Your password is updated. Taking you back to Elayne...");
      setTimeout(() => navigate(signInRoute), 2500);
    } catch (err) {
      setServerError("Your password wasn't updated. Try again.");
      sentryUtils.captureError(err, "Password reset failed");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <FullscreenLayout showTopBar>
        <div className="flex flex-1 items-center justify-center px-6">
          <div className="w-full max-w-lg text-center">
            <h1 className="mb-2">Password updated</h1>
            <p className="text-secondary mb-8">{success}</p>
            <Button
              onClick={() => navigate(signInRoute)}
              variant="brand"
              rounded
              className="w-full"
            >
              Go to log in
            </Button>
          </div>
        </div>
      </FullscreenLayout>
    );
  }

  return (
    <FullscreenLayout showTopBar>
      <div className="flex flex-1 items-center justify-center px-6">
        <div className="w-full max-w-lg">
          <h1 className="mb-2 text-center">Set a new password</h1>

          <p className="text-secondary mb-8 text-center">Enter your new password below</p>

          <div className="flex flex-col gap-4 mb-8">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="new-password" className="text-sm font-medium text-primary">
                New password
              </label>
              <Input
                id="new-password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setValidationError("");
                }}
                placeholder="Enter your new password"
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                disabled={!isValidLink}
                className={validationError && !password ? "border-destructive border-[2px]" : ""}
              />
              <FieldError message={validationError && !password ? validationError : null} />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="confirm-password" className="text-sm font-medium text-primary">
                Confirm password
              </label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setValidationError("");
                }}
                placeholder="Confirm your new password"
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                disabled={!isValidLink}
                className={
                  validationError && (!confirmPassword || password !== confirmPassword)
                    ? "border-destructive border-[2px]"
                    : ""
                }
              />
              <FieldError message={validationError && password ? validationError : serverError} />
            </div>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={loading || !isValidLink}
            variant="brand"
            rounded
            className="w-full"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Update password <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>

          {isValidLink && (
            <div className="mt-6">
              <SecurityNote text="Your information is encrypted and never sold." />
            </div>
          )}
        </div>
      </div>
    </FullscreenLayout>
  );
};

export default ResetPasswordPage;
